import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ModalService } from '@healthcatalyst/cashmere';
import { environment } from '../environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Event, NavigationEnd, Router } from '@angular/router';
import { OktaService } from './shared/services/okta.service';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';
import { AuthState, OktaAuth } from '@okta/okta-auth-js';

// Pointer to Google Analytics gTag function defined in index.html
declare let gtag: Function;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    today = new Date();
    appVersion = '';

    @ViewChild('aboutModal', {static: false})
    aboutModalTemplate: TemplateRef<unknown>;
    
    get isProd(): boolean {
        return environment.production;
    }

    get isViewer(): boolean {
        return this.router.url.includes('viewer') || this.router.url.includes('tech-catalog') || this.router.url.includes('changes');
    }

    constructor(
        private http: HttpClient,
        private router: Router,
        private modalService: ModalService,
        public oktaService: OktaService,
        private _oktaStateService: OktaAuthStateService,
        @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth
    ) {}
    
    ngOnInit(): void {
        // Listen for a login event from Okta
        this._oktaStateService.authState$.pipe(takeUntil(this.unsubscribe)).subscribe((state: AuthState) => {
            if ( state.isAuthenticated && state.accessToken ) {
                this.oktaService.accessToken = state.accessToken.accessToken;
                this.getUser();

                // Display the about modal on first view automatically
                const aboutCookie: boolean = this.getCookie( 'hc-catalog-about' ) !== 'true';
                if ( aboutCookie ) {
                    this.about(this.aboutModalTemplate);
                    this.setCookie( 'hc-catalog-about', 'true', 365 );
                }
            }
        });

        // Log a page view event on navigation for site analytics in Google Analytics
        this.router.events.pipe(takeUntil(this.unsubscribe)).subscribe((event: Event) => {
            if ( event instanceof NavigationEnd ) {
                if ( !event.url.includes('login/callback') && !event.url.includes('viewer') && this.oktaService.username.value ) {
                    gtag('event', 'page_view', {
                        page_title: 'Health Catalyst Product Catalog',
                        page_location: event.urlAfterRedirects,
                        user: this.oktaService.username.value
                    });
                }
            }
        });

        // Get the app's version number
        this.http.get<any>( './assets/app-config.json')
            .subscribe({
                next: data => {
                    this.appVersion = data.version;
                }
            });
    }

    // Retreive the user's metadata after they've logged in
    async getUser(): Promise<void> {
        return await this._oktaAuth.getUser()
            .then(userClaims => {
                this.oktaService.fullName = `${userClaims.given_name} ${userClaims.family_name}`;
                this.oktaService.username.next( `${userClaims.email}` );
                this.updateUserRole();

                // Log a page view on init only after we have a username
                if ( !this.router.url.includes('viewer') ) {
                    gtag('event', 'page_view', {
                        page_title: 'Health Catalyst Product Catalog',
                        page_location: this.router.url,
                        user: this.oktaService.username.value
                    });
                }
            });
    };

    // Determine if a user is an admin
    updateUserRole(): void {
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        this.http.get<any>( environment.azure.url + '/users/' + this.oktaService.username.value, options)
            .subscribe({
                next: data => {
                    if ( data.length > 0 ) {
                        this.oktaService.admin = data[0].role === 'super-admin' || data[0].role === 'admin';
                        this.oktaService.superAdmin.next( data[0].role === 'super-admin' );
                    } else {
                        this.oktaService.admin = false;
                        this.oktaService.superAdmin.next(false);
                    }
                },
                error: msg => { 
                    console.log( msg );
                }
            });
    }

    impersonateModeOn(): void {
        this.oktaService.admin = false;
        this.oktaService.impersonateMode = true;

        // Navigate to a blank page first to force a reload if navigating to the same page component
        this.router.navigate(['reload']).then(() => {
            this.router.navigate(['catalog']);
        });
    }

    impersonateModeOff(): void {
        this.oktaService.admin = true;
        this.oktaService.impersonateMode = false;
        
        // Navigate to a blank page first to force a reload if navigating to the same page component
        this.router.navigate(['reload']).then(() => {
            this.router.navigate(['catalog']);
        });
    }

    // Display about modal
    about(content: TemplateRef<unknown>): void {
        this.modalService.open(content);
    }

    // Log the user out of the app
    async logout(): Promise<void> {
        await this._oktaAuth.signOut({
            revokeAccessToken: false,
            revokeRefreshToken: false,
            clearTokensBeforeRedirect: true
        });
    }

    setCookie( cname: string, cvalue: string, exdays: number ) {
        const d = new Date();
        d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
        const expires = 'expires=' + d.toUTCString();
        document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
    }

    getCookie( cname: string ) {
        const name = cname + '=';
        const decodedCookie = decodeURIComponent( document.cookie );
        const ca = decodedCookie.split(';');
        for ( let i = 0; i < ca.length; i++ ) {
            let c = ca[i];
            while ( c.charAt(0) === ' ' ) {
                c = c.substring(1);
            }
            if ( c.indexOf(name) === 0 ) {
                return c.substring(name.length, c.length);
            }
        }
        return '';
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
